import { useState } from 'react'
import { ArrowLeft, ChevronRight, Folder, FolderOpen, Home, X } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { api, type MediaDirectoryListing, type MediaRoot } from '../lib/api'
import { ModalPortal } from './ModalPortal'

interface Props {
  root: MediaRoot
  currentValue: string
  onSelect: (path: string) => void
  onClose: () => void
}

export function DirectoryPickerDialog({ root, currentValue, onSelect, onClose }: Props) {
  const { t } = useTranslation()
  const [path, setPath] = useState('')
  const listing = useQuery({
    queryKey: ['media-root-directories', root.id, path],
    queryFn: () => api<MediaDirectoryListing>(`/api/v1/media-roots/${encodeURIComponent(root.id)}/directories?path=${encodeURIComponent(path)}`),
  })
  const segments = path ? path.split('/') : []

  return <ModalPortal><div className="modal-backdrop directory-picker-backdrop" onMouseDown={onClose}>
    <section className="modal-card directory-picker" onMouseDown={(event) => event.stopPropagation()}>
      <header className="modal-header"><div><span className="eyebrow"><FolderOpen />{t('directoryPicker.eyebrow')}</span><h2>{t('directoryPicker.title')}</h2></div><button className="icon-button" onClick={onClose}><X /></button></header>
      <p className="directory-picker-hint">{t('directoryPicker.hint', { root: root.label })}</p>
      {currentValue && <div className="directory-current-value"><span>{t('directoryPicker.current')}</span><strong>{root.label} / {currentValue}</strong></div>}
      <nav className="directory-breadcrumbs" aria-label={t('directoryPicker.breadcrumbs')}>
        <button onClick={() => setPath('')} title={root.label}><Home /><span>{root.label}</span></button>
        {segments.map((segment, index) => {
          const segmentPath = segments.slice(0, index + 1).join('/')
          return <span key={segmentPath}><ChevronRight /><button onClick={() => setPath(segmentPath)}>{segment}</button></span>
        })}
      </nav>
      <div className="directory-list" aria-busy={listing.isLoading}>
        {listing.isLoading && <div className="directory-picker-state">{t('common.loading')}</div>}
        {listing.error && <div className="inline-error">{listing.error.message}</div>}
        {listing.data?.parentPath !== null && <button className="directory-row directory-parent" onClick={() => setPath(listing.data?.parentPath ?? '')}><ArrowLeft /><span>{t('directoryPicker.parent')}</span></button>}
        {listing.data?.directories.map((directory) => <button className="directory-row" key={directory.path} onClick={() => setPath(directory.path)}><Folder /><span>{directory.name}</span><ChevronRight /></button>)}
        {listing.data && listing.data.directories.length === 0 && <div className="directory-picker-state">{t('directoryPicker.empty')}</div>}
      </div>
      {listing.data?.truncated && <small className="directory-picker-warning">{t('directoryPicker.truncated')}</small>}
      {listing.data && !listing.data.writable && <small className="directory-picker-warning">{t('directoryPicker.notWritable')}</small>}
      <footer className="modal-actions"><button className="quiet-button" onClick={onClose}>{t('common.cancel')}</button><button className="primary-button" disabled={!listing.data || listing.isLoading || !listing.data.writable} onClick={() => onSelect(listing.data?.path ?? path)}><FolderOpen />{t('directoryPicker.select')}</button></footer>
    </section>
  </div></ModalPortal>
}
